import {CurrentItem} from "./CurrentItem.js";
import {BaseEntity, cookies, EntityID, router} from "@intermesh/goui";
import {ServerApi} from "../util/ServerApi.js";
import {PortalConfig} from "../config.js";
import {t, Translate} from "../util/Translate.js";
import {IfwWindow} from "../components/IfwWindow.js";
import {ForcePasswordChangeDialog} from "../ForcePasswordChangeDialog.js";

export interface User extends BaseEntity {
	id: EntityID,
	deleted: boolean,
	displayName: string,
	email?: string,
	emailSecondary?: string,
	modules?: BaseEntity[],
	photoBlobId?: string,
	timezone: string,
	forcePasswordChange: boolean,
	permissions: {
		read?: boolean,
		write?: boolean,
		create?: boolean,
		manage?: boolean
	},
	usesSingleSignOn: boolean
}

declare global {
	var Android: any;
}

export class CurrentUser extends CurrentItem {
	public static userId?: number;
	static storageItem = "currentUser";
	static baseRoute = "elearning/auth";
	static returnProperties = "*,user[*]";

	public static async readIf(): Promise<any> {
		if (!sessionStorage.getItem(CurrentUser.storageItem)) {
			// Make sure that the portal config is reset periodically
			if (localStorage.getItem("portalConfig")) {
				const pc = JSON.parse(localStorage.getItem("portalConfig")!);
				if (!pc.expirasAt || pc.expirasAt < Date.now()) {
					localStorage.removeItem("portalConfig");
				}
			}
			if (cookies.get("accessToken") || !localStorage.getItem("portalConfig")) {
				const url = ServerApi.url(CurrentUser.baseRoute, {returnProperties: CurrentUser.returnProperties});
				const response = await ServerApi.call(url);
				const pc = {
					...PortalConfig, ...{
						generateCredentials: response.generateCredentials,
						mayRegister: response.mayRegister,
						mayUseOauth: response.mayUseOauth,
						mayUseSignin: response.mayUseSignin,
						mustAcceptTerms: response.mustAcceptTerms,
						notificationCount: response.notificationCount,
						openCourseSelection: response.openCourseSelection,
						expiresAt: Date.now() + (24 * 60 * 60 * 1000)
					}
				};
				pc.defaultSectionId = pc.defaultSectionId || response.defaultSectionId;

				localStorage.setItem("portalConfig", JSON.stringify(pc));

				// User appears to be logged in.
				if (response.success) {
					sessionStorage.removeItem("preventRedirectToStart");
					if (response.data.user) {
						sessionStorage.setItem(CurrentUser.storageItem, JSON.stringify(response.data.user));
						return CurrentUser.get();
					}
				} else {
					// This is an invalid accessToken cookie, so we remove it.
					if (cookies.get("accessToken")) {
						CurrentUser.logout();
					}
				}
			}

		}
		return CurrentUser.get();
	}

	public static get() {
		if (sessionStorage.getItem(CurrentUser.storageItem)) {
			return JSON.parse(sessionStorage.getItem(CurrentUser.storageItem)!);
		}
		return {};
	}

	public static logout() {
		Translate.clear();
		if (!cookies.get("accessToken")) {
			sessionStorage.clear();
			if (document.location.search) {
				let url = document.location.href;
				url = url.replace(document.location.search, "");
				location.replace(url);
			}
			void router.goto("");

			return;
		}
		const url = ServerApi.url('auth');

		ServerApi.call(url, {}, "DELETE").then((_response) => {
			const preventRedirect = sessionStorage.getItem("preventRedirectToStart");
			sessionStorage.clear();
			cookies.unset("accessToken", "/");
			// Github #533: some tablet devices use a non-standard browser. Instead of letting the owners choose
			// their own well-supported browsers, this was the 'solution' they came up with.
			if (typeof Android !== "undefined" && Android.restart) {
				// Restart the browser at the starting URL - for the IP Digital Signage platform only
				Android.restart();
			} else if (preventRedirect) {
				// In some cases, do NOT go back to start, e.g. when checking for existing authentications
				return;
			} else {
				// The OOTB supported Non-retarded way
				// Also make sure that any GET parameters are forgotten as well
				// let url = document.location.href;
				// url = url.replace(document.location.search, "");
				// location.replace(url);
				// void router.goto("");
				document.location = PortalConfig.frontendUrl;
			}

		});
	}

	public static async confirmLogout() {
		const confirm = await IfwWindow.confirm(t('Are you sure you want to logout?'), t('Confirm logout'));
		if (confirm) {
			CurrentUser.logout();
		}
	}

	public static forcePasswordChangePopupCheck(password: string | undefined) {
		const cu = CurrentUser.get();
		if (cu.forcePasswordChange) {
			if (!password) {
				CurrentUser.logout();
			}
			const fpc = new ForcePasswordChangeDialog({
				password: password
			});
			fpc.show();
		}
	}

	/**
	 * Check whether the currently logged in user has module access. The default permission type is 'read'
	 *
	 * @param moduleName
	 * @param packageName
	 * @param permission
	 */
	public static hasModule(moduleName: string, packageName = "Instructiefilm", permission = "read") {
		const cu = CurrentUser.get();

		const userModule = cu.modules.find((m: any): boolean => {
			return m.name === "GO\\Modules\\" + packageName + "\\" + moduleName + "\\Module";
		});
		if (!userModule) {
			return false;
		}
		return userModule.permissions[permission];
	}
}

export const currentUser = () => Object.assign(new CurrentUser());